<template>
  <Button
    v-bind="attrs"
    :as="as"
    :href="href"
    :target="linkTarget"
    :rel="linkRel"
    :type="resolvedButtonType"
    :severity="primevueSeverity"
    :size="props.size"
    :icon-pos="props.iconPosition"
    :disabled="props.disabled"
    class="mbz-button group items-center-safe"
    :class="[
      isRtl ? 'justify-end' : 'md:justify-start',
      props.size === 'large' ? 'px-6 pb-2' : 'py-2 px-4',
      props.disabled ? 'opacity-60' : '',
      props.severity === 'tertiary' ? 'rounded-[6px]' : '',
      `mbz-${severity}`,
      hasNoLabel && 'py-3',
    ]"
    :aria-label="
      isExternal && label ? `${label} (opens in a new tab)` : undefined
    "
    @click="handleClick"
  >
    <slot>
      <span
        v-if="props.label"
        class="button-label font-medium transition-colors duration-300 ease-in-out relative"
        :class="[props.iconPosition === 'right' ? 'order-first' : 'order-1']"
      >
        {{ props.label }}
      </span>
    </slot>

    <!-- If Icon has been passed in as a prop, render it here via slot -->
    <slot v-if="props.icon || isExternal" name="icon">
      <template v-if="isExternal">
        <MBZIcon
          v-if="props.icon"
          class="button-icon min-w-[14px] min-h-[14px]"
          :class="!hasNoLabel && iconAnimationClass"
          name="externalLink"
        />
      </template>
      <template v-else>
        <MBZIcon
          v-if="props.icon"
          class="button-icon min-w-[14px] min-h-[14px]"
          :class="!hasNoLabel && iconAnimationClass"
          :name="props.icon!"
        />
      </template>
    </slot>
  </Button>
</template>

<style lang="scss">
html[lang='ar'] .mbz-button,
html[dir='rtl'] .mbz-button {
  .button-icon {
    transform: rotate(180deg);
    right: 0;
    left: auto;
    top: 5px;
  }
}
</style>

<script setup lang="ts">
import type { IconName } from '@/components/global/MBZ/Icon.vue';
import type { Link } from '~/types';
import type { ButtonProps } from 'primevue';

const { isRtl } = useAppLocale();

defineOptions({
  inheritAttrs: false,
});

const emit = defineEmits<{ click: [event: MouseEvent] }>();

interface AppButtonProps extends /* @vue-ignore */ Omit<ButtonProps, 'icon'> {
  label?: string;
  iconPosition?: ButtonProps['iconPos'];
  size?: ButtonProps['size'];
  icon?: IconName | null;
  url?: Link;
  disabled?: boolean;
  severity?: ButtonProps['severity'];
}

const props = withDefaults(defineProps<AppButtonProps>(), {
  disabled: false,
  iconPosition: 'right',
  size: undefined,
  url: undefined,
  icon: 'arrow',
  label: '',
  severity: 'default',
});

const primevueSeverity = computed(() =>
  props.severity === 'tertiary' ? 'help' : props.severity
);

const hasNoLabel = computed(() => Boolean(props.label.length === 0));

const {
  attrs,
  href,
  isExternal,
  linkTarget,
  linkRel,
  resolvedButtonType,
  handleClick,
} = useCtaNavigation(() => props.url, emit);
const iconAnimationClass = getIconAnimationClass(props.icon);
const as = computed(() => (href.value ? 'a' : 'button'));
</script>

<style>
.mbz-button {
  border-width: 1px;
  border-style: solid;
  outline: 3px solid transparent;

  &:focus-visible {
    background-color: var(--tabbed-view);
    border: 1px solid transparent;
    outline: 3px solid var(--p-brand-dark-navy-500);
  }
}

/* PRIMARY */
.mbz-primary {
  .button-label {
    color: var(--color-surface-0);
  }

  .button-icon {
    color: var(--p-brand-sand-500);
  }

  &:focus-visible {
    .button-label {
      color: var(--p-brand-dark-navy-500);
    }
    .button-icon {
      color: var(--p-brand-sand-700);
    }
  }

  /* Navy background */
  :is([class*='mbzuai-navy'], [class*='pattern-navy']):not([class*='--hero'])
    & {
    background-color: var(--p-brand-sand-500);
    border-color: var(--p-brand-sand-500);

    .button-label {
      color: var(--p-brand-dark-navy-500);
    }

    .button-icon {
      color: var(--p-brand-navy-500);
    }

    &:hover {
      background-color: var(--p-brand-dark-navy-500);
      border-color: var(--color-white);

      .button-label {
        color: var(--color-surface-0);
      }

      .button-icon {
        color: var(--p-brand-sand-500);
      }
    }

    &:focus-visible {
      background-color: var(--tabbed-view);
      outline: 3px solid var(--color-white);

      .button-label {
        color: var(--p-brand-dark-navy-500);
      }

      .button-icon {
        color: var(--p-brand-sand-700);
      }
    }
  }
}

/* SECONDARY */
.mbz-secondary {
  &:focus-visible {
    .button-label {
      color: var(--p-brand-dark-navy-500);
    }
    .button-icon {
      color: var(--p-brand-sand-700);
    }
  }

  :is([class*='-sand']):not([class*='-sand-500']):not(
      [class*='-sand-300'],
      [class*='-sand-50']
    )
    & {
    background-color: var(--p-brand-sand-500);
    border-color: var(--p-brand-sand-500);

    &:hover {
      background-color: var(--p-brand-dark-navy-500);
      border-color: var(--p-brand-dark-navy-500);

      .button-icon {
        color: var(--p-brand-sand-500);
      }
    }

    &:focus-visible {
      background-color: var(--tabbed-view);
      outline: 3px solid var(--p-brand-dark-navy-500);

      .button-label {
        color: var(--p-brand-dark-navy-500);
      }

      .button-icon {
        color: var(--p-brand-sand-700);
      }
    }
  }

  /* Navy background */
  :is([class*='bg-mbzuai-navy'], [class*='pattern-navy']):not([class*='--hero'])
    & {
    background-color: var(--p-brand-sand-200);
    border-color: var(--p-brand-sand-200);

    .button-label {
      color: var(--p-brand-dark-navy-500);
    }

    .button-icon {
      color: var(--p-brand-navy-500);
    }

    &:hover {
      background-color: var(--p-brand-dark-navy-500);
      border-color: var(--p-brand-dark-navy-500);

      .button-label {
        color: var(--color-surface-0);
      }

      .button-icon {
        color: var(--p-brand-sand-500);
      }
    }

    &:focus-visible {
      background-color: var(--tabbed-view);
      outline: 3px solid var(--color-white);

      .button-label {
        color: var(--p-brand-dark-navy-500);
      }

      .button-icon {
        color: var(--p-brand-sand-700);
      }
    }
  }

  /* when on mobile nav */
  :is([class*='-500--hero']):not([class*='--hero']) & {
    background-color: var(--p-brand-sand-300);
  }
}

.mbz-tertiary {
  /* Navy background */
  :is([class*='bg-mbzuai-navy'], [class*='pattern-navy']):not([class*='--hero'])
    &:focus-visible {
    outline: 3px solid var(--color-white);
  }
}
</style>
